// RelationListView — embedded list-view renderer for O2M / M2M form fields
// (WS 4.3 Phase 1 + 2). Fetches the related child records on mount and
// renders them as a compact inline table. Columns come from:
//   1. `<field><ListView>…</ListView></field>` inline DSL declaration
//   2. Target model's default list view via `loadModelView`
//   3. Synthetic 2-col fallback (record_uuid + display_name)
//
// Edit-mode (Phase 2): when the parent form is in edit mode AND
// `onCommandsChange` is provided, the component:
//   - Renders an "Add line" affordance + per-row remove icon
//   - Click-to-edit on a row opens a popup form (Radix Dialog) backed by
//     the child's form view via `loadModelView(target, "form")`
//   - Accumulates buffered ops (PendingOp[]) → emits relational command
//     tuples back through `onCommandsChange` so the parent draft round-
//     trips them to the backend as `[[0,0,vals]] / [[1,uuid,vals]] /
//     [[2,uuid,0]] / [[3,uuid,0]]` at save time.
// The popup embeds FormSheet under its own RecordProvider whose
// onCreate / onUpdate are bufferer functions wired to this component's
// PendingOp state — so saving the popup never hits the backend; it just
// commits the draft to the parent's tuple-command list.

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Plus, Trash2, X } from "lucide-react";
import type { FieldDef, FormRenderPlan, ListColumn, ListRenderPlan, Json, RecordData } from "../core";
import { mapListPlan } from "../core/services/mappers";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useLocale } from "../react/providers/LocaleProvider";
import { toast } from "../react/hooks/use-toast";
import { widgets } from "../widgets";
import { RecordProvider } from "../react/form/RecordProvider";
import { useFormStateContext } from "../react/form/RecordProvider";
import { FormSheet } from "./FormSheet";
import {
    applyCreate,
    applyRemove,
    applyUpdate,
    makeTempIdGenerator,
    opsToCommands,
    type PendingOp,
    type RelationalCommand,
} from "./relationOps";

interface RelationListViewProps {
    /** Target model key (e.g. "crm.quote.version") — drives the view +
        record fetch. */
    targetModel: string;
    /** Raw field value from the parent draft. Accepts an array of UUID
        strings, an array of `{id, display_name, …}` dicts, or a mix; the
        component normalises to `relationIds`. */
    value: Json;
    /** Inline `<ListView>` declaration nested under the `<field>` element.
        When present, its columns take precedence over the child model's
        default list view. */
    inlineListView?: ListRenderPlan | null;
    /** Phase 2 — edit mode. When `true` AND `onCommandsChange` is set,
        the user can Add / Edit / Remove rows. Buffered ops emit through
        `onCommandsChange`. */
    isEditing?: boolean;
    /** Phase 2 — receives the current buffered command tuple list. The
        parent typically wires this to `onWriteField(fieldName, cmds)`
        on the form-state context. */
    onCommandsChange?: (cmds: RelationalCommand[]) => void;
    /** When set, remove emits `[3, uuid, 0]` (unlink) instead of
        `[2, uuid, 0]` (delete). Set this for M2M fields. */
    removeMode?: "delete" | "unlink";
    /** Parent form's record (its current draft). Used to seed the
        Add-line popup with sensible defaults — in particular, any child
        Reference field whose target model matches `parentModelKey`
        auto-fills to point back at the parent. */
    parentRecord?: RecordData;
    /** Parent form's model_key — used to detect which child Reference
        fields point back to the parent so they can be pre-filled. */
    parentModelKey?: string;
}

// Build the initial draft for an Add-line popup, seeded with sensible
// defaults derived from the parent record. Two seeding modes:
//
//   1. Back-reference auto-fill — for every child Reference field whose
//      `targetModel` matches the parent's model key, pre-fill with
//      `{id, display_name}` so the M2O picker shows the right parent
//      already selected. Covers the common case where O2M fields point
//      back at their owner (e.g. crm.quote.version → quote_id → crm.quote).
//   2. `parent_<key>` denormalized convention — for child fields named
//      `parent_<X>`, copy `parent[X]` straight through. Used by models
//      that store denormalized parent values for domain filters.
//
// Always sets `__parent__` so view predicates and substituted domains
// can reach `parent.X` tokens. Returns `null` when no parent context
// applies (let useFormState fall back to its default empty record).
export function buildAddLineDefaults(
    childFieldDefs: FieldDef[],
    parentRecord: RecordData | undefined,
    parentModelKey: string | undefined,
): RecordData | null {
    if (!parentRecord) return null;
    const parentId = String(parentRecord.record_uuid ?? parentRecord.id ?? "");
    // Fall back to the parent's record_uuid (if any) when display_name isn't
    // stamped yet — happens when the parent itself is a new record (no
    // backend read has computed display_name). Synthesise a sensible label
    // from the parent's most-likely "name" field so the M2O trigger isn't
    // blank in that case.
    const parentDisplayName = String(
        parentRecord.display_name
        ?? parentRecord.name
        ?? parentId
        ?? "",
    );
    const defaults: RecordData = { id: "", __parent__: parentRecord as unknown as Json };

    for (const fd of childFieldDefs) {
        // Back-reference auto-fill — fires even when the parent is itself a
        // new (unsaved) record. When `parentId` is empty, we still emit
        // `{id: "", display_name: "<parent label>"}` so the user sees the
        // link in the popup. At save time the backend's nested-tuple
        // handler injects the real parent uuid into every child create,
        // overriding the empty FK we shipped.
        if (parentModelKey && fd.type === "reference" && fd.targetModel === parentModelKey) {
            defaults[fd.name] = { id: parentId, display_name: parentDisplayName } as unknown as Json;
            continue;
        }
        if (fd.name.startsWith("parent_")) {
            const sourceKey = fd.name.slice("parent_".length);
            if (sourceKey in parentRecord) {
                defaults[fd.name] = parentRecord[sourceKey] as Json;
            }
        }
    }
    return defaults;
}

// Extract a flat list of UUIDs from a raw relational field value.
//
// IMPORTANT — the value can be in two shapes during the popup's lifetime:
//
//  1. **Server-loaded** — a list of record dicts (or bare UUID strings)
//     fetched by the presentation enrichment layer:
//       `[{record_uuid:"u1",…}, {record_uuid:"u2",…}, …]`
//     Every entry contributes its uuid.
//
//  2. **Buffered-edit** — a list of relational-command tuples emitted
//     by the popup's bufferer once the user starts adding / editing /
//     removing rows:
//       `[[1, "u2", {…}]]`  ← single UPDATE tuple, rows 1 + 3 untouched
//
//  Shape #2 carries ONLY the rows that have pending ops — untouched rows
//  vanish from the value. That's correct for the wire payload but BAD
//  for the table: if we re-fetch by these ids alone we lose the
//  untouched rows from the visible list. `useTableIds` below combines
//  the baseline (snapshotted on first load) with the tuple-mentioned
//  ids and applies removal semantics.
function extractIds(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    const out: string[] = [];
    for (const item of value) {
        if (item == null) continue;
        if (typeof item === "string") {
            out.push(item);
            continue;
        }
        if (typeof item === "object" && !Array.isArray(item)) {
            const obj = item as Record<string, unknown>;
            const id = obj.id ?? obj.record_uuid;
            if (id != null) out.push(String(id));
            continue;
        }
        if (Array.isArray(item) && item.length >= 2) {
            const op = item[0];
            if (op === 6 && Array.isArray(item[2])) {
                for (const id of item[2]) {
                    if (id != null) out.push(String(id));
                }
            } else if (typeof item[1] === "string") {
                out.push(item[1]);
            }
        }
    }
    return out;
}

// True when `value` is a list of relational-command tuples (CREATE /
// UPDATE / DELETE / UNLINK / LINK / SET / CLEAR — encoded as `[op,…]`).
// Identifies shape #2 from the `extractIds` docstring above.
function isTupleShape(value: unknown): boolean {
    if (!Array.isArray(value) || value.length === 0) return false;
    return value.every((item) => Array.isArray(item) && item.length >= 2 && typeof item[0] === "number");
}

// Return the set of uuids the tuple-shaped value EXPLICITLY removes from
// the baseline (DELETE = op 2, UNLINK = op 3). UPDATE / LINK keep the
// uuid in the visible set; CREATE / SET / CLEAR don't reference a
// baseline uuid.
function extractRemovedIds(value: unknown): Set<string> {
    const out = new Set<string>();
    if (!Array.isArray(value)) return out;
    for (const item of value) {
        if (Array.isArray(item) && item.length >= 2) {
            const op = item[0];
            if ((op === 2 || op === 3) && typeof item[1] === "string") {
                out.add(item[1]);
            } else if (op === 5) {
                // CLEAR — wipes the baseline entirely.
                return new Set(["__clear__"]);
            }
        }
    }
    return out;
}

export function RelationListView({
    targetModel,
    value,
    inlineListView,
    isEditing,
    onCommandsChange,
    removeMode = "delete",
    parentRecord,
    parentModelKey,
}: RelationListViewProps): ReactNode {
    const runtime = useRuntime();
    const { timezone } = useLocale();

    const [records, setRecords] = useState<RecordData[]>([]);
    const [columns, setColumns] = useState<ListColumn[]>(inlineListView?.columns ?? []);
    const [fieldDefs, setFieldDefs] = useState<FieldDef[]>([]);
    const [formPlan, setFormPlan] = useState<FormRenderPlan | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Phase 2 — buffered ops + popup-dialog state. Lives at this level
    // (not inside the dialog) so re-opens don't lose accumulated edits.
    const [pendingOps, setPendingOps] = useState<PendingOp[]>([]);
    const tempIdGen = useRef(makeTempIdGenerator());
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const [editingDraft, setEditingDraft] = useState<RecordData | null>(null);
    // Bumped on Save / Save & New so the popup's RecordProvider remounts with
    // a fresh draft even when editingRowId is unchanged (e.g. new → new).
    const [dialogNonce, setDialogNonce] = useState(0);

    // Snapshot the "originally loaded" relation uuids — taken the first
    // time `value` resolves to a non-tuple shape (server-loaded record
    // list). After the user makes any edit, `value` flips to the
    // buffered-tuple shape which references only changed rows; the
    // untouched majority would otherwise vanish from the table. We use
    // the baseline + tuple-removed-ids to compute the visible set
    // independently of `value`'s current shape.
    const baselineIdsRef = useRef<string[] | null>(null);
    const valueIsTupleShape = isTupleShape(value);
    if (!valueIsTupleShape && baselineIdsRef.current === null) {
        baselineIdsRef.current = extractIds(value);
    }
    const relationIds = useMemo(() => {
        if (!valueIsTupleShape) {
            return extractIds(value);
        }
        // Tuple shape — merge baseline with tuple-mentioned ids, then
        // subtract DELETE / UNLINK ids. CREATE temp ids are added so
        // that the table can show optimistic just-added rows (their
        // `records` row was already inserted by `onCreate`; we only need
        // to keep their id in the visible set so the row isn't culled).
        const removed = extractRemovedIds(value);
        if (removed.has("__clear__")) return [];
        const base = baselineIdsRef.current ?? [];
        const tupleIds = extractIds(value);
        return Array.from(new Set([...base, ...tupleIds])).filter((id) => !removed.has(id));
    }, [value, valueIsTupleShape]);
    const idsKey = relationIds.join(",");

    const emitOps = (next: PendingOp[]) => {
        setPendingOps(next);
        onCommandsChange?.(opsToCommands(next));
    };

    useEffect(() => {
        let alive = true;
        async function init() {
            setLoading(true);
            setError(null);
            try {
                let modelFieldDefs: FieldDef[] = [];
                let modelListColumns: ListColumn[] | null = null;
                let resolvedFormPlan: FormRenderPlan | null = null;
                if (!inlineListView) {
                    try {
                        const loaded = await runtime.services.action.loadModelView(targetModel, "list");
                        if (loaded) {
                            modelFieldDefs = loaded.fieldDefs;
                            const plan = loaded.view ? mapListPlan(loaded.view, loaded.fieldDefs) : null;
                            modelListColumns = plan?.columns ?? null;
                        }
                    } catch {
                        // fall through to synthetic columns
                    }
                }
                // Pre-fetch the form view too — popup dialogs open instantly
                // once it's cached. Cheap because most calls hit the same
                // backend route.
                try {
                    const formLoaded = await runtime.services.action.loadModelView(targetModel, "form");
                    if (formLoaded && formLoaded.view) {
                        resolvedFormPlan = formLoaded.view as FormRenderPlan;
                        // Use the form view's fieldDefs if richer (form
                        // typically exposes more fields than list).
                        if (formLoaded.fieldDefs.length > modelFieldDefs.length) {
                            modelFieldDefs = formLoaded.fieldDefs;
                        }
                    }
                } catch {
                    // Form view optional — Add Line will short-circuit if absent.
                }
                if (!alive) return;
                setFieldDefs(modelFieldDefs);
                setFormPlan(resolvedFormPlan);

                const resolvedColumns: ListColumn[] =
                    inlineListView?.columns
                    ?? modelListColumns
                    ?? [
                        { name: "record_uuid", label: "UUID", invisible: false },
                        { name: "display_name", label: "Name", invisible: false },
                    ];
                setColumns(resolvedColumns);

                if (relationIds.length === 0) {
                    setRecords([]);
                    setLoading(false);
                    return;
                }
                // Model-keyed search WITH presentation enrichment — goes
                // through `presentation.search_records_by_model` so M2O
                // columns arrive as `{id, display_name}` (and display_name
                // is stamped on each row). The kernel CRUD route would
                // skip those resolvers — see Enhancement 07.
                const result = await runtime.services.action.searchRecordsByModel(
                    targetModel,
                    { domain: [["record_uuid", "in", relationIds]], limit: relationIds.length },
                    "list",
                );
                if (!alive) return;
                const byId = new Map<string, RecordData>();
                for (const rec of result.records) {
                    byId.set(String(rec.record_uuid ?? rec.id ?? ""), rec);
                }
                const ordered = relationIds.map((id) => byId.get(id)).filter((r): r is RecordData => !!r);
                setRecords(ordered);
            } catch (err) {
                if (alive) setError(err instanceof Error ? err.message : String(err));
            } finally {
                if (alive) setLoading(false);
            }
        }
        init();
        return () => { alive = false; };
        // Deliberately depend on `idsKey` (a stable string), NOT on
        // `relationIds` (a useMemo'd array — gets a new reference each
        // time the parent's draft mutates, e.g. when a buffered create
        // tuple is appended to `value`). Tracking the array reference
        // would re-fire the fetch effect on every parent draft change,
        // overwriting optimistic in-memory rows added by `onCreate` /
        // `onUpdate` with an empty fetch result (CREATE tuples carry no
        // uuid → extractIds returns [] → setRecords([]) wipes the
        // optimistic line). idsKey only changes when the SET of resolved
        // ids actually changes, which is exactly when re-fetch is needed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetModel, idsKey, inlineListView, runtime]);

    if (error) {
        return <div className="relation-list relation-list--error">{error}</div>;
    }
    if (loading) {
        return <div className="relation-list relation-list--loading">Loading…</div>;
    }

    const fieldDefMap = new Map(fieldDefs.map((fd) => [fd.name, fd]));
    const visibleColumns = columns.filter((c) => !c.invisible);
    const canEdit = isEditing === true && onCommandsChange !== undefined;
    // The inline list-view declares `editable="top"`/`"bottom"` when its
    // author wants direct in-cell editing on the embedded table. In that
    // case the row click belongs to the inline editor (double-click to
    // enter cell edit), NOT a popup form — suppress the dialog path
    // entirely so the two affordances don't fight.
    const isInlineEditable = (inlineListView?.editable ?? "") !== "";
    // Row click opens the popup in BOTH parent modes:
    //   - parent is editing  → popup opens editable (saves buffer to parent)
    //   - parent is read-only → popup opens read-only (browse view)
    // Provided the child has a form view AND inline-editable wasn't set.
    const canOpenDialog = formPlan !== null && !isInlineEditable;

    const handleRemove = (rowId: string) => {
        const next = applyRemove(pendingOps, rowId, removeMode);
        emitOps(next);
        setRecords((prev) => prev.filter((r) => String(r.record_uuid ?? r.id ?? "") !== rowId));
    };

    const handleRowClick = async (rec: RecordData) => {
        if (!canOpenDialog) return;
        const rowId = String(rec.record_uuid ?? rec.id ?? "");
        // Fetch the FORM-shaped enriched record BEFORE opening the dialog
        // so the popup's RecordProvider mounts with `quote_id = {id,
        // display_name}` etc. already in the draft. Opening optimistically
        // with the list-shape and re-seeding later doesn't work — once the
        // popup mounts in edit mode, useFormState's "don't clobber typing"
        // guard suppresses the initialRecord re-seed, so the optimistic
        // bare-UUID M2O sticks. Fetch is small (single record).
        let enriched: RecordData | null = null;
        try {
            enriched = await runtime.services.action.readRecordByModel(targetModel, rowId, "form");
        } catch {
            // Backend miss / network — fall back to the list-shaped row
            // (M2O fields will show the muted UUID placeholder but the
            // dialog still opens so the user can edit other fields).
        }
        setEditingRowId(rowId);
        setEditingDraft(enriched ?? rec);
        setDialogOpen(true);
    };

    const handleAddLine = () => {
        if (!canOpenDialog) return;
        setEditingRowId(null);
        setEditingDraft(buildAddLineDefaults(fieldDefs, parentRecord, parentModelKey));
        setDialogOpen(true);
    };

    // Buffered create — invoked by the popup's RecordProvider on Save
    // when no existing row was being edited (new line).
    //
    // `values` is the wire-shape payload (M2O extracted to bare uuids,
    // M2M as set-tuples) used for the relational command tuple stored
    // on the parent's draft. `draft` is the original pre-extraction
    // shape ({id, display_name} for M2O) — used for the optimistic
    // table row so M2O cells render display_name instead of raw uuid.
    const onCreate = async (values: Record<string, Json>, draft?: RecordData): Promise<RecordData> => {
        const tempId = tempIdGen.current();
        emitOps(applyCreate(pendingOps, tempId, values));
        const optimistic: RecordData = {
            // Prefer draft (with display labels) so the table shows
            // human-readable M2O cells. Fall back to extracted values
            // if no draft was supplied (e.g. an older caller).
            ...(draft ?? values),
            record_uuid: tempId,
            id: tempId,
            display_name: deriveDisplayName(values, draft),
        };
        setRecords((prev) => [...prev, optimistic]);
        // NB: closing / resetting the dialog is decided by the footer
        // (Save vs Save & New vs Save & Close) via handleAfterSave.
        return optimistic;
    };
    // Buffered update — same bufferer pattern, just merges into pending
    // create / update for the same row.
    const onUpdate = async (recordRef: string, values: Record<string, Json>, draft?: RecordData): Promise<RecordData> => {
        emitOps(applyUpdate(pendingOps, recordRef, values));
        const existing = records.find((r) => String(r.record_uuid ?? r.id ?? "") === recordRef);
        const merged: RecordData = {
            ...(existing ?? {}),
            // draft carries the display-shaped values for changed M2O fields —
            // preferred over the extracted `values` for the same reason as
            // onCreate above.
            ...(draft ?? values),
            record_uuid: recordRef,
            id: recordRef,
            display_name: deriveDisplayName(values, existing),
        };
        setRecords((prev) => prev.map((r) => (String(r.record_uuid ?? r.id ?? "") === recordRef ? merged : r)));
        // Closing / resetting handled by the footer via handleAfterSave.
        return merged;
    };

    // What to do once a popup save has buffered its op successfully:
    //   - "close" → Save: dismiss the dialog (back to the line list)
    //   - "new"   → Save & New: reset to a fresh blank line, dialog stays open
    const handleAfterSave = (mode: "close" | "new") => {
        if (mode === "new") {
            setEditingRowId(null);
            setEditingDraft(buildAddLineDefaults(fieldDefs, parentRecord, parentModelKey));
            setDialogNonce((n) => n + 1);
            return;
        }
        setDialogOpen(false);
    };

    const isEmpty = records.length === 0;

    return (
        <div className="relation-list">
            {isEmpty ? (
                <div className="relation-list--empty">No records</div>
            ) : (
                <table className="relation-list__table">
                    <thead>
                        <tr>
                            {visibleColumns.map((col) => (
                                <th key={col.name} className="relation-list__th">{col.label || col.name}</th>
                            ))}
                            {canEdit ? <th className="relation-list__th relation-list__th--actions" /> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((rec) => {
                            const rowId = String(rec.record_uuid ?? rec.id ?? "");
                            return (
                                <tr
                                    key={rowId}
                                    className={`relation-list__row${canOpenDialog ? " relation-list__row--clickable" : ""}`}
                                    onClick={canOpenDialog ? () => handleRowClick(rec) : undefined}
                                >
                                    {visibleColumns.map((col) => {
                                        const fd = fieldDefMap.get(col.name);
                                        const w = widgets.resolve(col.widget, fd?.type);
                                        const cellValue = rec[col.name];
                                        return (
                                            <td key={col.name} className="relation-list__cell">
                                                {w?.render
                                                    ? w.render({
                                                        value: cellValue as Json | undefined,
                                                        record: rec,
                                                        column: col,
                                                        field: fd,
                                                        timezone,
                                                    })
                                                    : (cellValue == null || cellValue === ""
                                                        ? <span className="cell-muted">—</span>
                                                        : <span>{String(cellValue)}</span>)}
                                            </td>
                                        );
                                    })}
                                    {canEdit ? (
                                        <td className="relation-list__cell relation-list__cell--actions">
                                            <button
                                                type="button"
                                                className="relation-list__remove"
                                                title={removeMode === "unlink" ? "Remove" : "Delete"}
                                                onClick={(e) => { e.stopPropagation(); handleRemove(rowId); }}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </td>
                                    ) : null}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {canEdit && canOpenDialog ? (
                <div className="relation-list__footer">
                    <button type="button" className="relation-list__add" onClick={handleAddLine}>
                        <Plus size={13} /> <span>Add line</span>
                    </button>
                </div>
            ) : null}

            {canOpenDialog ? (
                <RelationPopupDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    targetModel={targetModel}
                    formPlan={formPlan}
                    fieldDefs={fieldDefs}
                    editingRecord={editingDraft}
                    editingRowId={editingRowId}
                    onCreate={onCreate}
                    onUpdate={onUpdate}
                    onAfterSave={handleAfterSave}
                    dialogNonce={dialogNonce}
                    parentEditing={canEdit}
                    parentModelKey={parentModelKey}
                />
            ) : null}
        </div>
    );
}

// Derive a best-effort display name for an optimistic row in the table
// after a popup save. Prefers an explicit `display_name`, then `name`,
// then falls back to merging with the previous record's display_name.
function deriveDisplayName(values: Record<string, unknown>, prev?: RecordData): string {
    if (values.display_name != null) return String(values.display_name);
    if (values.name != null) return String(values.name);
    if (prev?.display_name != null) return String(prev.display_name);
    return "(new row)";
}

// Tracks how deeply nested the current relation popup is, starting at 0
// for the outermost. Each `RelationPopupDialog` reads the current value
// and provides `value + 1` to its descendants. Drives the per-depth
// z-index modifier so the inner popup's overlay actually sits above the
// outer popup's content (real backdrop blur), and the inner popup's
// content sits above the inner overlay. Without this every popup uses
// the same z-index → Radix portals stack by DOM order → the parent
// content paints above the nested overlay (overlays group first, then
// content group) → no backdrop blur behind the nested popup.
const DialogDepthContext = createContext<number>(0);

// Popup form dialog — wraps an embedded FormSheet in a Radix Dialog with
// its own RecordProvider. The provider's onCreate / onUpdate are the
// bufferer functions on the parent RelationListView; saving the popup
// commits to the buffer, NEVER hits the backend.
function RelationPopupDialog({
    open,
    onOpenChange,
    targetModel,
    formPlan,
    fieldDefs,
    editingRecord,
    editingRowId,
    onCreate,
    onUpdate,
    onAfterSave,
    dialogNonce,
    parentEditing,
    parentModelKey,
}: {
    open: boolean;
    onOpenChange: (next: boolean) => void;
    targetModel: string;
    formPlan: FormRenderPlan | null;
    fieldDefs: FieldDef[];
    editingRecord: RecordData | null;
    editingRowId: string | null;
    onCreate: (values: Record<string, Json>) => Promise<RecordData>;
    onUpdate: (recordRef: string, values: Record<string, Json>) => Promise<RecordData>;
    /** Post-save transition: dismiss the dialog ("close") or reset to a fresh line ("new"). */
    onAfterSave: (mode: "close" | "new") => void;
    /** Bumped by the parent to force a fresh RecordProvider on Save / Save & New. */
    dialogNonce: number;
    /** When false, the popup renders read-only — Save button suppressed,
        only "Close" shown in the footer. The parent form must be in edit
        mode for buffered ops to flow up. */
    parentEditing: boolean;
    /** Parent model_key — used to auto-detect back-reference FKs on the
        child fieldDefs so required-validation skips them without the
        view author having to declare `invisible="1"`. */
    parentModelKey?: string;
}): ReactNode {
    const isNew = editingRowId === null;
    const verb = parentEditing ? (isNew ? "Create" : "Edit") : "View";
    const title = `${verb} ${humanizeModel(targetModel)}`;
    // Read current depth (0 = outermost popup), then provide depth+1 to
    // descendants. Z-index lifts by 20 per level so the inner popup's
    // overlay (z=200+20*N) renders ABOVE the outer popup's content
    // (z=201+20*(N-1)) — real backdrop blur for the inner popup.
    const depth = useContext(DialogDepthContext);
    const overlayZ = 200 + 20 * depth;
    const contentZ = 201 + 20 * depth;
    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="theme-dialog__overlay" style={{ zIndex: overlayZ }} />
                <Dialog.Content
                    className="relation-dialog"
                    style={{ zIndex: contentZ }}
                    // Suppress Radix's default "click outside closes the dialog"
                    // behavior. Without this, opening a NESTED popup (e.g. an
                    // O2M Add Line inside this popup) causes a click inside
                    // the nested dialog to be interpreted as an outside-click
                    // for THIS dialog, collapsing the whole stack. The X
                    // button + footer Cancel/Discard remain the explicit
                    // close paths. Escape still closes the topmost dialog
                    // (Radix routes the Escape to whichever dialog is on
                    // top of its focus stack, so nested popups dismiss
                    // correctly without affecting parents).
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}>
                    {/* Every descendant popup reads depth+1 so its z-index lifts above ours. */}
                    <DialogDepthContext.Provider value={depth + 1}>
                    <header className="relation-dialog__head">
                        <Dialog.Title className="relation-dialog__title">{title}</Dialog.Title>
                        <Dialog.Close className="theme-dialog__close" aria-label="Close">
                            <X size={16} />
                        </Dialog.Close>
                    </header>
                    <Dialog.Description className="theme-dialog__sr-only">{title}</Dialog.Description>
                    {formPlan ? (
                        <RecordProvider
                            key={`${editingRowId ?? "new"}:${dialogNonce}`}
                            record={editingRecord}
                            isNew={isNew && parentEditing}
                            // When the parent form is editing, the popup
                            // also opens in edit mode from the first paint
                            // — the user already committed to editing by
                            // clicking Edit on the parent, so the click-
                            // existing-row step shouldn't add a second
                            // Edit-button click. Read-mode browsing only
                            // applies when the parent is itself read-only.
                            initialIsEditing={parentEditing}
                            fieldDefs={fieldDefs}
                            viewFieldNames={null}
                            recordRef={isNew ? undefined : editingRowId ?? undefined}
                            // Form-sheet elements feed required-validation's
                            // invisible-aware skip so back-references hidden
                            // via `invisible="1"` don't block save (their
                            // value is wired by the backend tuple handler).
                            formElements={formPlan.sheet}
                            // Auto-detect back-reference FKs — any Reference
                            // field on the child whose target equals the
                            // parent's model is, by definition, a back-ref.
                            // Skip it from required-validation regardless of
                            // whether the view author marked it invisible="1".
                            // The backend nested-tuple handler always
                            // injects this column on the child create, so
                            // the user never has to pick it manually.
                            extraInvisibleFields={
                                parentModelKey
                                    ? fieldDefs
                                        .filter((fd) => fd.type === "reference" && fd.targetModel === parentModelKey)
                                        .map((fd) => fd.name)
                                    : []
                            }
                            onCreate={onCreate}
                            onUpdate={onUpdate}
                        >
                            {/* `form-view--revamp` — the popup embeds a bare FormSheet
                                (no FormView wrapper), so it must opt into the revamp
                                fact-cell/section language itself or the dialog renders
                                the legacy dense grid, inconsistent with the page form. */}
                            <div className="relation-dialog__body form-view--revamp">
                                <FormSheet
                                    elements={formPlan.sheet}
                                    fieldDefs={fieldDefs}
                                    actionSlug={targetModel}
                                    modelKey={targetModel}
                                    recordRef={isNew ? null : editingRowId}
                                />
                            </div>
                            <RelationDialogFooter
                                onClose={() => onOpenChange(false)}
                                onAfterSave={onAfterSave}
                                parentEditing={parentEditing}
                            />
                        </RecordProvider>
                    ) : (
                        <div className="relation-dialog__body">
                            <div className="relation-list--empty">No form view defined for {targetModel}</div>
                        </div>
                    )}
                    </DialogDepthContext.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

// Footer for the popup form — carries the form-view cycle scoped to the
// popup's own RecordProvider. Reads state from useFormStateContext.
//
// Modes:
//   - parentEditing = false       → read-only browse, just [Close]
//   - read mode (existing record) → [Edit] [Close]
//   - edit mode                   → [Save] [Save & New] [Discard]
//
// Both Save variants buffer the same op (via form.onSave → onCreate/onUpdate);
// they differ only in the post-save transition handed to `onAfterSave`:
// "close" dismisses the dialog (plain Save), "new" resets to a blank line
// (Save & New). Discard closes without buffering.
function RelationDialogFooter({
    onClose,
    onAfterSave,
    parentEditing,
}: {
    onClose: () => void;
    onAfterSave: (mode: "close" | "new") => void;
    parentEditing: boolean;
}) {
    const form = useFormStateContext();

    // Browse-only path — parent form isn't editing, so no save can flow.
    if (!parentEditing) {
        return (
            <footer className="relation-dialog__foot">
                <button type="button" className="btn relation-dialog__cancel" onClick={onClose}>
                    Close
                </button>
            </footer>
        );
    }

    const runSave = async (mode: "close" | "new") => {
        const result = await form.onSave();
        if (result.ok) {
            onAfterSave(mode);
            return;
        }
        // Surface the failure instead of doing nothing — mirrors the main
        // form's FormControlPanel. Required-field misses are also painted red
        // inline by FormSheet (form.invalidFields); the toast names them so a
        // hidden / off-screen required field isn't an invisible dead-end.
        if (result.missing && result.missing.length > 0) {
            toast({
                variant: "destructive",
                title: result.missing.length === 1
                    ? "1 required field missing"
                    : `${result.missing.length} required fields missing`,
                description: result.missing.join(", "),
            });
        } else {
            toast({
                variant: "destructive",
                title: "Could not save",
                description: result.error,
            });
        }
    };
    const handleDiscard = () => {
        // Discard always closes the dialog. Unsaved dialog edits are never
        // buffered up (only Save buffers via form.onSave), so closing simply
        // drops them — the parent's line list is left untouched.
        form.onDiscard();
        onClose();
    };

    if (!form.isEditing) {
        return (
            <footer className="relation-dialog__foot">
                <button type="button" className="btn relation-dialog__save" onClick={form.onEdit}>
                    Edit
                </button>
                <button type="button" className="btn relation-dialog__cancel" onClick={onClose}>
                    Close
                </button>
            </footer>
        );
    }

    const busy = form.isSaving;
    return (
        <footer className="relation-dialog__foot">
            <button
                type="button"
                className="btn relation-dialog__save"
                onClick={() => void runSave("close")}
                disabled={busy}
            >
                {busy ? "Saving…" : "Save"}
            </button>
            <button
                type="button"
                className="btn"
                onClick={() => void runSave("new")}
                disabled={busy}
            >
                Save &amp; New
            </button>
            <button
                type="button"
                className="btn relation-dialog__cancel"
                onClick={handleDiscard}
                disabled={busy}
            >
                Discard
            </button>
        </footer>
    );
}

function humanizeModel(modelKey: string): string {
    const last = modelKey.split(".").pop() ?? modelKey;
    return last.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
